import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CardContent,
    Chip,
    Divider,
    Stack,
    Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import GavelIcon from '@mui/icons-material/Gavel';
import PaymentIcon from '@mui/icons-material/Payment';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { LoadingButton } from '@mui/lab';
import { Confirm, Notify } from 'notiflix';
import React, { useState } from 'react';
import styled from 'styled-components';
import moneyFormat from '../../../../hooks/moneyFormat';
import useIndex from '../../../../hooks/useIndex';
import useWompiCheckout from '../../../../hooks/useWompiCheckout';
import validErrors from '../../../../hooks/validErrors';
import {
    approveAgreement,
    cancelAgreement,
    checkoutAgreementWompi,
    downloadAgreementPdf,
    signAgreementAsCompany,
    signAgreementAsFenabocol,
} from '../../../../data/api/agreements/AgreementRoute';
import SignaturePadModal from '../../../../components/modal/SignaturePadModal';

const STATUS_PENDING_SIGNATURES = 'Pendiente de firmas';
const STATUS_FULLY_SIGNED = 'Firmado';
const STATUS_APPROVED = 'Aprobado';
const STATUS_CANCELLED = 'Cancelado';
const STATUS_REJECTED = 'Rechazado';

const TERMINAL_STATUSES = [STATUS_APPROVED, STATUS_CANCELLED, STATUS_REJECTED];

const STATUS_STYLES = {
    [STATUS_PENDING_SIGNATURES]: {
        color: '#8a6500',
        bg: 'rgba(214, 170, 58, 0.18)',
        border: 'rgba(214, 170, 58, 0.38)',
    },
    [STATUS_FULLY_SIGNED]: {
        color: '#10285f',
        bg: 'rgba(16, 40, 95, 0.10)',
        border: 'rgba(16, 40, 95, 0.22)',
    },
    [STATUS_APPROVED]: {
        color: '#0f5c36',
        bg: 'rgba(46, 125, 50, 0.12)',
        border: 'rgba(46, 125, 50, 0.25)',
    },
    [STATUS_CANCELLED]: {
        color: '#9b1c1c',
        bg: 'rgba(211, 47, 47, 0.10)',
        border: 'rgba(211, 47, 47, 0.25)',
    },
    [STATUS_REJECTED]: {
        color: '#9b1c1c',
        bg: 'rgba(211, 47, 47, 0.10)',
        border: 'rgba(211, 47, 47, 0.25)',
    },
};

const CompanyAgreementDesc = ({
    item,
    loadData,
    loadUser,
    handleExpandAgreement,
    agreementPanel,
}) => {
    const { token, auth } = useIndex();
    const { openCheckout, loading: wompiLoading } = useWompiCheckout();

    const [submit, setSubmit] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [signingRole, setSigningRole] = useState(null);

    const isAdmin = String(auth?.role_id) === String(process.env.REACT_APP_ADMIN_ROLE);
    const isOwner = auth?.id === item?.user_id;
    const isTerminal = TERMINAL_STATUSES.includes(item?.status);
    const isWompiPayment = String(item?.income_type_name ?? '').toLowerCase() === 'wompi';

    const canSignAsCompany = isOwner && !item?.company_signed && !isTerminal;
    const canSignAsFenabocol = isAdmin && !item?.fenabocol_signed && !isTerminal;

    const canApprove =
        isAdmin &&
        item?.company_signed &&
        item?.fenabocol_signed &&
        item?.status === STATUS_FULLY_SIGNED;

    const canPayWompi =
        isOwner &&
        isWompiPayment &&
        item?.status === STATUS_FULLY_SIGNED;

    const canCancel = (isAdmin || isOwner) && !isTerminal;

    const statusStyle = STATUS_STYLES[item?.status] ?? {
        color: '#10285f',
        bg: 'rgba(16, 40, 95, 0.08)',
        border: 'rgba(16, 40, 95, 0.16)',
    };

    const handleDownload = async () => {
        setDownloading(true);

        try {
            await downloadAgreementPdf(token, item?.uuid, `contrato_${item?.id}.pdf`);
        } catch (error) {
            validErrors(error);
        } finally {
            setDownloading(false);
        }
    };

    const handlePayWompi = async () => {
        await openCheckout(
            null,
            { startFn: (token) => checkoutAgreementWompi(token, item?.uuid) }
        );

        loadData?.();
        loadUser?.();
    };

    const handleSaveSignature = async (role, dataUrl) => {
        try {
            const signFn = role === 'company'
                ? signAgreementAsCompany
                : signAgreementAsFenabocol;

            const { data } = await signFn(token, item?.uuid, dataUrl);

            if (data?.success) {
                Notify.success(data?.message ?? 'Firma registrada.');
                setSigningRole(null);
                loadData?.();
            }
        } catch (error) {
            validErrors(error);
        }
    };

    const preApprove = () => {
        Confirm.show(
            'Aprobar contrato',
            'Al aprobar se entregarán los códigos a la empresa. Esta acción es irreversible. ¿Continuar?',
            'Sí, aprobar',
            'No',
            () => approveNow(),
            () => { }
        );
    };

    const approveNow = async () => {
        setSubmit(true);

        try {
            const { data } = await approveAgreement(token, item?.uuid);

            if (data?.success) {
                Notify.success(data?.message ?? 'Contrato aprobado.');
                loadData?.();
                loadUser?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    const preCancel = () => {
        Confirm.show(
            'Cancelar contrato',
            '¿Seguro deseas cancelar este contrato?',
            'Sí',
            'No',
            () => cancelNow(),
            () => { }
        );
    };

    const cancelNow = async () => {
        setSubmit(true);

        try {
            const { data } = await cancelAgreement(token, item?.uuid);

            if (data?.success) {
                Notify.success(data?.message ?? 'Contrato cancelado.');
                loadData?.();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setSubmit(false);
        }
    };

    return (
        <StyledAccordion
            expanded={agreementPanel === item?.uuid}
            onChange={handleExpandAgreement(item?.uuid)}
            disableGutters
        >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <SummaryContent>
                    <Box className="left">
                        <Box className="icon-box">
                            <DescriptionOutlinedIcon />
                        </Box>

                        <Box className="info-stack">
                            <Typography className="title">
                                Contrato #{item?.id}
                            </Typography>

                            <Typography className="company-name">
                                <BusinessOutlinedIcon fontSize="inherit" />
                                {item?.company_name ?? 'Empresa sin nombre'}
                                {item?.company_document ? ` · NIT ${item.company_document}` : ''}
                            </Typography>

                            <Typography className="subtitle">
                                {item?.moves_amount ?? 0} jugadas · {moneyFormat(item?.agreement_amount ?? 0)} {item?.currency}
                            </Typography>
                        </Box>
                    </Box>

                    <StatusPill
                        $color={statusStyle.color}
                        $bg={statusStyle.bg}
                        $border={statusStyle.border}
                    >
                        {item?.status}
                    </StatusPill>
                </SummaryContent>
            </AccordionSummary>

            <AccordionDetails>
                <ContentCard>
                    <CardContent>
                        <InfoGrid>
                            <InfoItem>
                                <BusinessOutlinedIcon />
                                <Box>
                                    <span>Empresa</span>
                                    <strong>{item?.company_name ?? '-'}</strong>
                                </Box>
                            </InfoItem>

                            <InfoItem>
                                <BadgeOutlinedIcon />
                                <Box>
                                    <span>NIT / Documento</span>
                                    <strong>{item?.company_document ?? '-'}</strong>
                                </Box>
                            </InfoItem>

                            <InfoItem>
                                <DescriptionOutlinedIcon />
                                <Box>
                                    <span>Tipo de contrato</span>
                                    <strong>{item?.agreement_type ?? '-'}</strong>
                                </Box>
                            </InfoItem>

                            <InfoItem>
                                <PaymentsOutlinedIcon />
                                <Box>
                                    <span>Valor del contrato</span>
                                    <strong>
                                        {moneyFormat(item?.agreement_amount ?? 0)} {item?.currency ?? ''}
                                    </strong>
                                </Box>
                            </InfoItem>

                            <InfoItem>
                                <EventOutlinedIcon />
                                <Box>
                                    <span>Fecha de creación</span>
                                    <strong>{item?.created_at ?? '-'}</strong>
                                </Box>
                            </InfoItem>

                            <InfoItem>
                                <ConfirmationNumberOutlinedIcon />
                                <Box>
                                    <span>Jugadas</span>
                                    <strong>{item?.moves_amount ?? 0}</strong>
                                </Box>
                            </InfoItem>
                        </InfoGrid>

                        <StyledDivider>
                            <span>Firmas</span>
                        </StyledDivider>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip
                                size="small"
                                icon={<CheckCircleIcon />}
                                label={item?.company_signed ? 'Empresa firmó' : 'Empresa pendiente'}
                                sx={{
                                    fontWeight: 900,
                                    color: item?.company_signed ? '#0f5c36' : '#8a6500',
                                    background: item?.company_signed
                                        ? 'rgba(46, 125, 50, 0.12)'
                                        : 'rgba(214, 170, 58, 0.16)',
                                    border: item?.company_signed
                                        ? '1px solid rgba(46, 125, 50, 0.25)'
                                        : '1px solid rgba(214, 170, 58, 0.28)',
                                }}
                            />

                            <Chip
                                size="small"
                                icon={<CheckCircleIcon />}
                                label={item?.fenabocol_signed ? 'Fenabocol firmó' : 'Fenabocol pendiente'}
                                sx={{
                                    fontWeight: 900,
                                    color: item?.fenabocol_signed ? '#0f5c36' : '#8a6500',
                                    background: item?.fenabocol_signed
                                        ? 'rgba(46, 125, 50, 0.12)'
                                        : 'rgba(214, 170, 58, 0.16)',
                                    border: item?.fenabocol_signed
                                        ? '1px solid rgba(46, 125, 50, 0.25)'
                                        : '1px solid rgba(214, 170, 58, 0.28)',
                                }}
                            />

                            {item?.status === STATUS_APPROVED && (
                                <Chip
                                    size="small"
                                    label="Códigos entregados"
                                    sx={{
                                        fontWeight: 900,
                                        color: '#0f5c36',
                                        background: 'rgba(46, 125, 50, 0.12)',
                                        border: '1px solid rgba(46, 125, 50, 0.25)',
                                    }}
                                />
                            )}
                        </Stack>

                        <ActionsBox>
                            <LoadingButton
                                variant="outlined"
                                size="small"
                                loading={downloading}
                                onClick={handleDownload}
                                startIcon={<DownloadIcon />}
                                sx={outlinedButtonStyles}
                            >
                                <span>Descargar contrato</span>
                            </LoadingButton>

                            {canSignAsCompany && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setSigningRole('company')}
                                    sx={primaryButtonStyles}
                                >
                                    Firmar como empresa
                                </Button>
                            )}

                            {canSignAsFenabocol && (
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => setSigningRole('fenabocol')}
                                    sx={primaryButtonStyles}
                                >
                                    Firmar como Fenabocol
                                </Button>
                            )}

                            {canPayWompi && (
                                <LoadingButton
                                    variant="contained"
                                    size="small"
                                    loading={wompiLoading}
                                    onClick={handlePayWompi}
                                    startIcon={<PaymentIcon />}
                                    sx={successButtonStyles}
                                >
                                    <span>Pagar con Wompi</span>
                                </LoadingButton>
                            )}

                            {canApprove && (
                                <LoadingButton
                                    variant="contained"
                                    size="small"
                                    loading={submit}
                                    onClick={preApprove}
                                    startIcon={<GavelIcon />}
                                    sx={successButtonStyles}
                                >
                                    <span>Aprobar y entregar códigos</span>
                                </LoadingButton>
                            )}

                            {canCancel && (
                                <LoadingButton
                                    variant="outlined"
                                    size="small"
                                    loading={submit}
                                    onClick={preCancel}
                                    sx={dangerButtonStyles}
                                >
                                    <span>Cancelar contrato</span>
                                </LoadingButton>
                            )}
                        </ActionsBox>
                    </CardContent>
                </ContentCard>
            </AccordionDetails>

            <SignaturePadModal
                open={signingRole === 'company'}
                onClose={() => setSigningRole(null)}
                title="Firma como empresa"
                description={`Confirmas la firma del contrato ${item?.id} en representación de ${item?.company_name}.`}
                onSave={(dataUrl) => handleSaveSignature('company', dataUrl)}
            />

            <SignaturePadModal
                open={signingRole === 'fenabocol'}
                onClose={() => setSigningRole(null)}
                title="Firma como Fenabocol"
                description={`Confirmas la firma del contrato ${item?.id} en representación de Fenabocol.`}
                onSave={(dataUrl) => handleSaveSignature('fenabocol', dataUrl)}
            />
        </StyledAccordion>
    );
};

export default CompanyAgreementDesc;

const primaryButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
    '&:hover': {
        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
    },
};

const outlinedButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    color: '#10285f',
    borderColor: 'rgba(214, 170, 58, 0.75)',
    background: '#fff',
    '&:hover': {
        borderColor: '#d6aa3a',
        background: 'rgba(214, 170, 58, 0.12)',
    },
};

const successButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    background: 'linear-gradient(90deg, #0f7b45 0%, #169b5b 100%)',
    boxShadow: '0 6px 14px rgba(15, 123, 69, 0.18)',
    '&:hover': {
        background: 'linear-gradient(90deg, #169b5b 0%, #0f7b45 100%)',
    },
};

const dangerButtonStyles = {
    borderRadius: '14px',
    fontWeight: 900,
    textTransform: 'uppercase',
    color: '#9b1c1c',
    borderColor: 'rgba(211, 47, 47, 0.45)',
    background: '#fff',
    '&:hover': {
        borderColor: '#d32f2f',
        background: 'rgba(211, 47, 47, 0.08)',
    },
};

const StyledAccordion = styled(Accordion)`
    margin-bottom: 0rem !important;
    border-radius: 18px !important;
    overflow: hidden;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: #fff !important;
    box-shadow: 0 8px 18px rgba(15, 34, 77, 0.06) !important;

    &::before {
        display: none;
    }

    &:hover {
        border-color: rgba(214, 170, 58, 0.55);
    }

    .MuiAccordionSummary-root {
        min-height: 72px;
        background: linear-gradient(180deg, #ffffff 0%, #f8faff 100%);
    }

    .MuiAccordionSummary-content {
        margin: 0.75rem 0;
    }

    .MuiAccordionDetails-root {
        padding: 0 1rem 1rem;
        background: #f8faff;
    }

    .MuiSvgIcon-root {
        color: #10285f;
    }
`;

const SummaryContent = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;

    .left {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        min-width: 0;
    }

    .icon-box {
        width: 42px;
        height: 42px;
        border-radius: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;
    }

    .info-stack {
        min-width: 0;
        display: flex;
        flex-direction: column;
    }

    .title {
        color: #10285f;
        font-weight: 900;
        font-size: 1rem;
        line-height: 1.2;
    }

    .company-name {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        margin-top: 0.2rem;
        color: #10285f;
        font-size: 0.9rem;
        font-weight: 800;
        line-height: 1.2;
        word-break: break-word;

        svg {
            color: #d6aa3a;
            font-size: 1rem;
        }
    }

    .subtitle {
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.85rem;
        font-weight: 700;
        margin-top: 0.15rem;
    }

    @media (max-width: 700px) {
        align-items: flex-start;
        flex-direction: column;
    }
`;

const StatusPill = styled.span`
    padding: 0.42rem 0.8rem;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 900;
    white-space: nowrap;
    color: ${({ $color }) => $color};
    background: ${({ $bg }) => $bg};
    border: 1px solid ${({ $border }) => $border};
`;

const ContentCard = styled.div`
    border-radius: 18px;
    border: 1px solid rgba(15, 34, 77, 0.1);
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    overflow: hidden;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.8rem;
    margin-bottom: 1.2rem;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const InfoItem = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    padding: 0.85rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    min-width: 0;

    svg {
        color: #d6aa3a;
        font-size: 1.25rem;
        flex-shrink: 0;
    }

    span {
        display: block;
        color: rgba(15, 34, 77, 0.6);
        font-size: 0.78rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.35px;
    }

    strong {
        display: block;
        margin-top: 0.15rem;
        color: #10285f;
        font-size: 0.9rem;
        font-weight: 900;
        word-break: break-word;
    }
`;

const StyledDivider = styled.div`
    margin: 0.5rem 0 1rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.3rem 0.8rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        font-size: 0.78rem;
    }
`;

const ActionsBox = styled(Box)`
    display: flex;
    gap: 0.7rem;
    margin-top: 1.2rem;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        button {
            width: 100%;
        }
    }
`;